/**
 * Time and date formatting.
 *
 * Everything is computed in the business timezone, not the browser's. The legacy
 * app set `date_default_timezone_set` per controller and disagreed with itself —
 * Backend_jobs used Australia/Melbourne, Backend_newdash used Australia/Sydney —
 * which matters for a scheduling system on the Victoria/NSW border and around
 * DST transitions.
 */

export const TIMEZONE = 'Australia/Melbourne';

/** The legacy `greeting()` helper in my_common_helper.php, with a fixed timezone. */
export function getGreeting(now: Date = new Date()): string {
    const hour = Number.parseInt(
        new Intl.DateTimeFormat('en-AU', { hour: 'numeric', hour12: false, timeZone: TIMEZONE }).format(now),
        10,
    );
    if (hour < 12) {
        return 'Good morning';
    }
    if (hour < 17) {
        return 'Good afternoon';
    }
    return 'Good evening';
}

export function formatLongDate(now: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: TIMEZONE,
    }).format(now);
}

export function formatClock(now: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: TIMEZONE,
    }).format(now);
}

/**
 * "24 Sep 2026" from an instant, in the business timezone.
 *
 * For timestamptz values such as `signed_at`. Slicing the first ten characters
 * off the ISO string would give the UTC date, which is the previous day for
 * anything recorded before 10am Melbourne — a SWMS signed at 8am would read as
 * signed yesterday.
 */
export function formatInstantAu(value: string | Date): string {
    const at = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(at.getTime())) {
        return '—';
    }
    return new Intl.DateTimeFormat('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: TIMEZONE,
    }).format(at);
}
